import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../context/CompanyContext';
import api from '../../services/api';
import './Retentions.css';

const Retentions = () => {
  const { companyId } = useCompany();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    retentionType: 'renta',
    thirdPartyId: '',
    invoiceId: '',
    baseAmount: 0,
    retentionDate: new Date().toISOString().split('T')[0]
  });

  // Obtener retenciones
  const { data, isLoading, error } = useQuery({
    queryKey: ['retentions', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/retentions`);
      return response.data.data;
    },
    enabled: !!companyId
  });

  // Obtener terceros
  const { data: thirdParties } = useQuery({
    queryKey: ['thirdParties', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/third-parties`);
      return response.data.data.thirdParties || [];
    },
    enabled: !!companyId
  });

  // Obtener facturas
  const { data: invoices } = useQuery({
    queryKey: ['invoices', companyId],
    queryFn: async () => {
      const response = await api.get(`/companies/${companyId}/invoices`);
      return response.data.data.invoices || [];
    },
    enabled: !!companyId
  });

  // Crear retención
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/companies/${companyId}/retentions`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['retentions', companyId]);
      setShowForm(false);
      setFormData({
        retentionType: 'renta',
        thirdPartyId: '',
        invoiceId: '',
        baseAmount: 0,
        retentionDate: new Date().toISOString().split('T')[0]
      });
    }
  });

  // Generar certificado
  const generateCertificateMutation = useMutation({
    mutationFn: async (retentionId) => {
      const response = await api.post(`/companies/${companyId}/retentions/${retentionId}/generate-certificate`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['retentions', companyId]);
      alert('Certificado generado exitosamente');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  if (isLoading) return <div>Cargando retenciones...</div>;
  if (error) return <div>Error al cargar retenciones</div>;

  return (
    <div className="retentions">
      <div className="page-header">
        <h2>Retenciones en la Fuente</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancelar' : '+ Nueva Retención'}
        </button>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>Nueva Retención</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Tipo de Retención *</label>
                <select
                  value={formData.retentionType}
                  onChange={(e) => setFormData({ ...formData, retentionType: e.target.value })}
                  required
                >
                  <option value="renta">Renta</option>
                  <option value="iva">IVA</option>
                  <option value="ica">ICA</option>
                  <option value="cree">CREE</option>
                </select>
              </div>
              <div className="form-group">
                <label>Fecha *</label>
                <input
                  type="date"
                  value={formData.retentionDate}
                  onChange={(e) => setFormData({ ...formData, retentionDate: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Tercero *</label>
              <select
                value={formData.thirdPartyId}
                onChange={(e) => setFormData({ ...formData, thirdPartyId: e.target.value })}
                required
              >
                <option value="">Seleccionar...</option>
                {thirdParties?.map(tp => (
                  <option key={tp.id} value={tp.id}>
                    {tp.nit} - {tp.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Factura (Opcional)</label>
              <select
                value={formData.invoiceId}
                onChange={(e) => setFormData({ ...formData, invoiceId: e.target.value })}
              >
                <option value="">Ninguna</option>
                {invoices?.filter(inv => inv.thirdParty?.id === formData.thirdPartyId).map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoiceNumber}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Base Gravable *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.baseAmount}
                onChange={(e) => setFormData({ ...formData, baseAmount: e.target.value })}
                required
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isLoading}>
                {createMutation.isLoading ? 'Creando...' : 'Crear Retención'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="retentions-list">
        <h3>Lista de Retenciones ({data?.pagination?.total || 0})</h3>
        {data?.retentions && data.retentions.length > 0 ? (
          <table className="retentions-table">
            <thead>
              <tr>
                <th>Certificado</th>
                <th>Tipo</th>
                <th>Tercero</th>
                <th>Base</th>
                <th>%</th>
                <th>Retención</th>
                <th>Fecha</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {data.retentions.map(retention => (
                <tr key={retention.id}>
                  <td>{retention.certificateNumber || '-'}</td>
                  <td>
                    <span className={`type-badge type-${retention.retentionType}`}>
                      {retention.retentionType.toUpperCase()}
                    </span>
                  </td>
                  <td>{retention.thirdParty?.name}</td>
                  <td>${retention.baseAmount.toFixed(2)}</td>
                  <td>{retention.percentage}%</td>
                  <td>${retention.retentionAmount.toFixed(2)}</td>
                  <td>{new Date(retention.retentionDate).toLocaleDateString()}</td>
                  <td>
                    <span className={`status-badge status-${retention.status}`}>
                      {retention.status}
                    </span>
                  </td>
                  <td>
                    {retention.status === 'pendiente' && (
                      <button
                        onClick={() => generateCertificateMutation.mutate(retention.id)}
                        className="btn-link"
                        disabled={generateCertificateMutation.isLoading}
                      >
                        Generar Certificado
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <p>No hay retenciones registradas</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Retentions;

